// 대행 요청 상세 패널 — 신규/진행중/완료/보류 4세그먼트 + 완료 장부(CancelAgencyDoneList) 공용.
// 행을 눌러 열리는 우측 슬라이드(모바일 전폭). 지금까지는 상세 화면이 없어 부가서비스 등 신청 내역을
// 확인할 방법이 없었다 — 이 패널이 그 자리를 채운다 (Jin 확정, 2026-09-17).
//
// 서버 계약 GET /cancel-requests/:id 는 S7 트랙이 동시 구현 중이라 아직 미배포일 수 있다.
// 404/405/오류면 목록 행(props.request) 데이터 + RequestServicesEditor 자체 조회만으로 축약 표시한다
// (진행 타임라인·검증 상세·말소증 여부는 detail 응답 전용이라 축약 모드에선 감춘다).
//
// 상태 변경/서류 시트/수정 폼은 새로 만들지 않고 CancelAgency.js 가 이미 가진 조각을 그대로 받아 쓴다
// (statusSelect = reqStatusSelect, onOpenDocs = openDocSheet, onEdit = startEditReq) — 로직 이중화 방지.
import React, { useState, useEffect } from 'react';
import { T, R } from '../v2/theme';
import SlidePanel from '../v2/kit/SlidePanel';
import { API, authFetch } from '../utils/api';
import RequestServicesEditor from './RequestServicesEditor';
import MalsoCertViewer from './MalsoCertViewer';
import { docReqSummary } from './CancelAgencyDocReqSheet';
import { pathLabel } from './cancelBrokerBands';

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
var dash = function (v) { return (v === null || v === undefined || v === '') ? '-' : v; };

var secTitleSt = { fontSize: 12.5, fontWeight: 600, color: T.navy, margin: '18px 0 8px' };
var dividerSt = { border: 'none', borderTop: '1px solid ' + T.bd, margin: '14px 0 0' };
var labelSt = { fontSize: 11, fontWeight: 600, color: T.muted };
var valueSt = { fontSize: 13, color: T.ink, marginTop: 2, wordBreak: 'break-word' };

// 라벨/값 1행 — 없는 값은 '-' (스펙 4번 규칙)
function Field(props) {
  return (<div style={{ minWidth: 0 }}>
    <div style={labelSt}>{props.label}</div>
    <div style={valueSt}>{dash(props.value)}</div>
  </div>);
}

function FieldGrid(props) {
  return (<div style={{ display: 'grid', gridTemplateColumns: props.isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>{props.children}</div>);
}

function SectionTitle(props) { return (<div style={secTitleSt}>{props.children}</div>); }

// 진행 타임라인 — 있는 스텝만 순서대로. requestDate 는 폴백에서도 항상 있다.
function timelineSteps(d, row) {
  var steps = [];
  var push = function (label, v) { if (v) steps.push({ label: label, v: v }); };
  push('신청', (d && d.requestDate) || row.requestDate);
  push('접수', d && d.acceptedAt);
  push('수거', d && d.pickedUpAt);
  if ((d && d.status) === '취소' || row.status === '취소') push('취소', (d && d.cancelledAt) || '');
  else push('완료', (d && d.completedDate) || row.completedDate);
  return steps.filter(function (s) { return s.v; });
}

function CancelRequestDetailPanel(props) {
  var open = !!props.open;
  var row = props.request || {};
  var isMobile = !!props.isMobile;
  var isStaff = !!props.isStaff;
  var isCancelAgency = !!props.isCancelAgency;
  var docList = props.docList || [];
  var showToast = props.showToast || function () {};

  var [detail, setDetail] = useState(null);   // GET /cancel-requests/:id 응답 (없으면 null = 폴백)
  var [viewCert, setViewCert] = useState(false);
  // 거래처 바꾸기 — 대행사 하위 거래처 신청을 YK 운영자가 같은 계정의 다른 거래처로 (완료 전까지)
  var [changing, setChanging] = useState(false);
  var [units, setUnits] = useState(null);   // GET /agency-clients (바꾸기를 누른 뒤에만 — 직원은 403)
  var [pickId, setPickId] = useState(0);
  var [busy, setBusy] = useState(false);

  var reqId = row.id;
  useEffect(function () {
    setDetail(null); setViewCert(false); setChanging(false);
    if (!open || !reqId) return undefined;
    var alive = true;
    authFetch(API + '/cancel-requests/' + reqId).then(function (r) {
      if (r.status === 404 || r.status === 405) return null;
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (d) { if (alive && d) setDetail(d); }).catch(function () {});
    return function () { alive = false; };
  }, [open, reqId]);

  if (!open) return null;

  var car = (detail && detail.car) || {};
  var req = (detail && detail.request) || {};
  var fallback = !detail; // 축약 모드 — detail 미도착(404/405/오류/로딩중) 이면 row 만으로 표시

  var carNo = row.carNo || car.carNo;
  var carType = row.carType || car.carType;
  var statusLabel = row.status || req.status || '-';
  var companyName = isCancelAgency ? (req.companyName || row.requesterCompanyName) : (req.agencyName || row.agencyName);

  var docSummary = docReqSummary(docList, row.carId);
  var hasMalsoCert = !fallback && !!detail.hasMalsoCert;

  var steps = timelineSteps(fallback ? null : req, row);

  // 거래처 라벨 키(unitName·accountName…)는 목록 행이 최신(상태·거래처 변경 뒤 loadAll 로 갱신) — 상세 응답보다 우선한다.
  var lab = Object.assign({}, req, row);
  var showUnit = isCancelAgency && lab.accountKind === 'broker';
  var canChange = showUnit && !isStaff && lab.requesterIsBroker === true && lab.status !== '완료' && lab.status !== '취소';
  var curUnitId = parseInt(lab.unitId, 10) || 0;
  var unitOpts = (units || []).filter(function (u) {
    return Number(u.accountId) === Number(lab.accountId) && Number(u.active) === 1;
  });
  var startChange = function () {
    setChanging(true); setPickId(curUnitId);
    if (units === null) {
      authFetch(API + '/agency-clients').then(function (r) { return r.json(); })
        .then(function (d) { setUnits(Array.isArray(d) ? d : []); }).catch(function () { setUnits([]); });
    }
  };
  var applyChange = function () {
    if (busy || !pickId || pickId === curUnitId) return;
    setBusy(true);
    var body = { unitId: pickId };
    if (row.version !== undefined && row.version !== null) body.version = row.version;
    authFetch(API + '/cancel-requests/' + reqId + '/unit', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (d) { return { status: r.status, data: d || {} }; }); })
      .then(function (res) {
        var d = res.data; setBusy(false);
        if (res.status === 409) {
          // VERSION_CONFLICT · ALREADY_COMPLETED · 취소 — 행이 이미 바뀌었다: 최신 행을 다시 받는다
          showToast(d.code === 'VERSION_CONFLICT' ? (d.error || '다른 사용자가 이미 수정했습니다') + ' — 최신 내용으로 다시 불러왔습니다' : (d.error || '이미 처리된 요청입니다'), 'err');
          if (props.onChanged) props.onChanged();
        } else if (d.success) {
          showToast('거래처를 바꿨습니다'); setChanging(false);
          if (props.onChanged) props.onChanged();
        } else showToast(d.error || '실패', 'err');
      }).catch(function () { setBusy(false); showToast('서버 오류', 'err'); });
  };

  return (<SlidePanel
    open={open}
    onClose={props.onClose}
    isMobile={isMobile}
    width={640}
    title={carNo || '-'}
    subtitle={[carType, statusLabel].filter(Boolean).join(' · ')}
    badge={props.badges}
    footer={[
      <React.Fragment key="status">{props.statusSelect ? props.statusSelect(row) : null}</React.Fragment>,
      !isStaff && <button key="edit" type="button" className="btn btn-outline btn-sm" onClick={function () { if (props.onEdit) props.onEdit(row); }}>수정</button>,
      <div key="sp" style={{ flex: 1 }} />,
      <button key="close" type="button" className="btn btn-outline btn-sm" onClick={props.onClose}>닫기</button>
    ]}
  >
    <SectionTitle>차량</SectionTitle>
    <FieldGrid isMobile={isMobile}>
      <Field label="차량번호" value={carNo} />
      <Field label="차종" value={carType} />
      <Field label="차대번호" value={row.vin || car.vin} />
      <Field label="소유자" value={row.owner || car.owner} />
      <Field label="주소" value={car.ownerAddr} />
      <Field label="등록일" value={car.regDate} />
      <Field label="차량 상태" value={car.status} />
    </FieldGrid>
    <hr style={dividerSt} />

    <SectionTitle>신청</SectionTitle>
    <FieldGrid isMobile={isMobile}>
      {showUnit && <Field label="거래처" value={<span>{pathLabel(lab)}
        {canChange && !changing && <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={startChange}>바꾸기</button>}
      </span>} />}
      <Field label={isCancelAgency ? '수출업체' : '대행업체'} value={companyName} />
      <Field label="말소 사유" value={row.cancelType} />
      <Field label="접수 방식" value={req.intakeMethod} />
      <Field label="신청일" value={row.requestDate || req.requestDate} />
      {!isStaff && <Field label="수수료" value={(row.fee || req.fee) ? fmt(row.fee || req.fee) + '원' : null} />}
      <Field label="픽업 주소" value={req.pickupAddress} />
    </FieldGrid>
    {changing && (<div style={{ marginTop: 10, border: '1px solid ' + T.bd, borderRadius: R.md, padding: 12, background: T.head }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.navy, marginBottom: 6 }}>{'거래처 바꾸기 · ' + lab.accountName}</div>
      {units === null ? <div style={valueSt}>불러오는 중…</div> : unitOpts.map(function (u) {
        return (<label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 2px', fontSize: 13, color: T.ink, cursor: 'pointer' }}>
          <input type="radio" name="ca-unit-pick" checked={pickId === u.id} onChange={function () { setPickId(u.id); }} />
          {u.name === lab.accountName ? u.name + ' (본인)' : u.name}
        </label>);
      })}
      <div style={{ fontSize: 11.5, color: T.muted, margin: '6px 0 10px' }}>거래처는 완료 전까지 바꿀 수 있습니다. 바꾸면 청구·단가가 새 거래처를 따릅니다.</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-blue btn-sm" disabled={busy || !pickId || pickId === curUnitId} onClick={applyChange}>바꾸기</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={function () { setChanging(false); }}>취소</button>
      </div>
    </div>)}
    {(row.memo || req.memo) && <div style={{ marginTop: 8 }}><Field label="메모" value={row.memo || req.memo} /></div>}
    <hr style={dividerSt} />

    <SectionTitle>부가서비스</SectionTitle>
    <RequestServicesEditor requestId={reqId} agencyId={row.agencyId || req.agencyId} showFee={!isStaff} readOnly={false} showToast={showToast} />
    <hr style={dividerSt} />

    <SectionTitle>진행</SectionTitle>
    {steps.length > 0 ? (<div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
      {steps.map(function (s, i) { return (<Field key={i} label={s.label} value={s.v} />); })}
    </div>) : <div style={valueSt}>-</div>}
    <FieldGrid isMobile={isMobile}>
      <Field label="담당 직원" value={row.staffName || req.assignedStaffName} />
      {(row.status === '보류' || req.status === '보류') && <Field label="보류 사유" value={req.holdReason} />}
      {(row.status === '보류' || req.status === '보류') && <Field label="보류 전 상태" value={req.holdPrevStatus} />}
      {(row.status === '취소' || req.status === '취소') && <Field label="취소 사유" value={req.cancelReason} />}
    </FieldGrid>
    <hr style={dividerSt} />

    {(row.lien_status || row.eligibility_status || req.lien || req.eligibility) && (<React.Fragment>
      <SectionTitle>검증</SectionTitle>
      <FieldGrid isMobile={isMobile}>
        {(row.lien_status || (req.lien && req.lien.status)) && <Field label="압류/저당" value={row.lien_detail || (req.lien && req.lien.detail) || (row.lien_status === 'has_lien' ? '있음' : '없음')} />}
        {(row.eligibility_status || (req.eligibility && req.eligibility.status)) && <Field label="자격 검증" value={row.eligibility_detail || (req.eligibility && req.eligibility.detail) || row.eligibility_status} />}
      </FieldGrid>
      <hr style={dividerSt} />
    </React.Fragment>)}

    <SectionTitle>서류</SectionTitle>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {hasMalsoCert && <button type="button" className="btn btn-outline btn-sm" onClick={function () { setViewCert(true); }}
        style={{ background: T.okTint, borderColor: T.okBd, color: T.ok, fontWeight: 700 }}>말소증 보기</button>}
      <button type="button" className="btn btn-outline btn-sm" onClick={function () { if (props.onOpenDocs) props.onOpenDocs(row); }} title={docSummary.title}
        style={docSummary.tone === 'blue' ? { background: T.accentTint, borderColor: T.accentBd, color: T.accentPress, fontWeight: 700 } : undefined}>
        {docSummary.label}
      </button>
    </div>

    {viewCert && <MalsoCertViewer
      title={'말소증' + (carNo ? ' — ' + carNo : '')}
      carNo={carNo}
      fetchDoc={function () { return authFetch(API + '/cancel-requests/' + reqId + '/malso-cert'); }}
      share={function () { return authFetch(API + '/cancel-requests/' + reqId + '/malso-share', { method: 'POST' }).then(function (r) { return r.json(); }); }}
      showToast={showToast}
      onClose={function () { setViewCert(false); }}
    />}
  </SlidePanel>);
}

export default CancelRequestDetailPanel;
